import LinkedArray from './LinkedArray.ts';

export type DeferCallback = (error?: Error | null) => void;
export type DeferFunction = (calback: DeferCallback) => void;
export type AwaitCallback = (error?: Error | null) => void;

interface QueueState {
  parallelism: number;
  tasks: LinkedArray<DeferFunction>;
  runningCount: number;
  error: Error | null;
  awaitCalled: boolean;
  awaitCallback: AwaitCallback | undefined;
  flushing: boolean;
}

export default class Queue {
  private _state: QueueState;

  constructor(parallelism: number = Infinity) {
    this._state = {
      parallelism,
      tasks: new LinkedArray(),
      runningCount: 0,
      error: null,
      awaitCallback: undefined,
      awaitCalled: false,
      flushing: false,
    };
    this._callAwait = this._callAwait.bind(this);
    this._callDefer = this._callDefer.bind(this);
  }

  private _callAwait() {
    if (this._state.awaitCalled || !this._state.awaitCallback) return;
    this._state.awaitCalled = true;
    return this._state.awaitCallback(this._state.error);
  }

  private _flush() {
    this._state.flushing = true;
    while (!this._state.error && this._state.tasks.length && this._state.runningCount < this._state.parallelism) {
      this._state.runningCount++;
      this._state.tasks.shift()?.(this._callDefer);
    }
    this._state.flushing = false;
    if (this._state.error || !(this._state.tasks.length + this._state.runningCount)) {
      this._callAwait();
    }
  }

  private _callDefer(err?: Error | null) {
    this._state.runningCount--;
    if (err && !this._state.error) this._state.error = err;
    if (this._state.flushing) return;
    this._flush();
  }

  defer(defer: DeferFunction) {
    if (this._state.error) return;
    if (this._state.runningCount < this._state.parallelism) {
      this._state.runningCount++;
      defer(this._callDefer);
    } else this._state.tasks.push(defer);
  }

  await(callback: AwaitCallback) {
    if (this._state.awaitCallback) throw new Error(`Awaiting callback was added twice: ${callback}`);
    this._state.awaitCallback = callback;
    if (this._state.error || !(this._state.tasks.length + this._state.runningCount)) return this._callAwait();
  }
}
